import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChildren,
  QueryList,
  SimpleChanges,
  OnChanges,
  ViewChild,
  ElementRef,
  NgZone,
  AfterViewInit,
  Inject,
  DOCUMENT
} from '@angular/core';
import {
  MapInfoWindow,
  MapMarker,
  MapDirectionsService,
  ClusterIconStyle,
  GoogleMap
} from '@angular/google-maps';
import {
  EnterpriseSearchService,
  HotelItinerary,
  WithSubscriptionComponent,
  HotelAvalibilityService,
  ServiceType
} from '@sabstravtech/obtservices/angular';
import {
  HotelAvalibilityQuoteResult,
  HotelEnterpriseSearchInterface
} from '@sabstravtech/obtservices/base';

import { faCar, faPersonWalking, faArrowsUpDown } from '@fortawesome/free-solid-svg-icons';

import { ThemeService } from '../../../../core/services/theme.service';

declare let google: any;
@Component({
  selector: 'app-map-hotels',
  templateUrl: './map-hotels.component.html',
  styleUrls: ['./map-hotels.component.css'],
  standalone: false
})
export class MapHotelsComponent
  extends WithSubscriptionComponent
  implements OnInit, AfterViewInit, OnChanges
{
  @Output() afterBackToList: EventEmitter<null> = new EventEmitter();
  @Input() arrayOfHotels: HotelItinerary[];
  @Input()
  mapControl: {
    showMap: boolean;
    hotelChosen: number | boolean;
    hotelCodeChosen: string;
    ratesUpdated?: EventEmitter<boolean>;
    filterChange?: EventEmitter<HotelItinerary[]>;
    choseHotel?: EventEmitter<HotelItinerary>;
  };
  @Input() noOfRooms: number;
  @ViewChild(GoogleMap) map: GoogleMap;
  // @ViewChildren(AgmMarker) markers: QueryList<AgmMarker>;
  directionsResults$: Observable<google.maps.DirectionsResult | undefined>;
  mapOptions: google.maps.MapOptions = {};
  referencePoint: google.maps.LatLngLiteral;
  locations: google.maps.LatLngLiteral[] = [];
  label: string;
  error = false;
  centerInterval: any;
  clusterStyles: ClusterIconStyle[] = [
    {
      height: 50,
      width: 50,
      textColor: 'white',
      url: `https://images.sabscorp.com/images/enterprise/lightUK/assets/png/${this.themeService.getMapClusterIcon()}`,
      fontWeight: 'bold',
      textSize: 20,
      anchorText: [13, 0]
    }
  ];
  locationPinURL =
    'https://images.sabscorp.com/images/enterprise/lightUK/assets/png/hotel-map-pin-blue.png';
  directionsPanelOpen = false;
  directionsOrigin: any = null;
  directionsOriginName: string = null;
  directionsDestination: any = null;
  directionsDestinationName: string = null;
  directionsMode: string = google.maps.TravelMode.DRIVING;
  directionsURL = '';
  faCar = faCar;
  faPersonWalking = faPersonWalking;
  faArrowsUpDown = faArrowsUpDown;
  public searchParams: HotelEnterpriseSearchInterface;
  lastOpenedWindow: MapInfoWindow = null;

  @ViewChild('fromInput') fromInputRef: ElementRef;
  @ViewChild('toInput') toInputRef: ElementRef;
  @ViewChild('directionsPanel') directionsPanelElementRef: ElementRef;
  @ViewChild(MapInfoWindow) infoWindow: MapInfoWindow;
  constructor(
    public mapDirectionsService: MapDirectionsService,
    public searchService: EnterpriseSearchService,
    // private mapsAPILoader: MapsAPILoader,
    private ngZone: NgZone,
    protected hotelAvailabilityService: HotelAvalibilityService,
    public themeService: ThemeService,
    @Inject(DOCUMENT) private document: Document
  ) {
    super();
    this.mapControl = {
      showMap: false,
      hotelChosen: null,
      hotelCodeChosen: null
    };
    this.searchParams = searchService.searches[ServiceType.Hotel];
  }

  ngOnInit(): void {
    const { location, office, postcode, trainsStation } = this.searchParams;
    this.referencePoint = {
      lat: location
        ? location.latitude
        : office
          ? office.latitude
          : postcode
            ? postcode.latitude
            : trainsStation
              ? trainsStation.latitude
              : null,
      lng: location
        ? location.longitude
        : office
          ? office.longitude
          : postcode
            ? postcode.longitude
            : trainsStation
              ? trainsStation.longitude
              : null
    };
    this.updateLocations();
    this.subscribe(this.mapControl.filterChange, (hotels) => {
      if (hotels) {
        this.updateLocations(hotels);
      }
    });
    this.centerInterval = setInterval(() => {
      if (this.mapControl.hotelChosen === false) {
        return;
      }
      let hotel: HotelItinerary;
      if (this.arrayOfHotels.length === 1) {
        hotel = this.arrayOfHotels[0];
      } else {
        const index = +this.mapControl.hotelChosen;
        hotel = Number.isInteger(index) ? this.arrayOfHotels[index] : undefined;
      }
      if (hotel) {
        this.updateLocations([hotel]);
      }
      clearInterval(this.centerInterval);
    }, 100);
  }

  ngAfterViewInit(): void {
    this.setupAddressSearch(google.maps.TravelMode.DRIVING);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.arrayOfHotels?.currentValue) {
      this.updateLocations(changes.arrayOfHotels.currentValue);
    }
  }

  updateLocations(hotels: HotelItinerary[] = this.arrayOfHotels): void {
    setTimeout(() => {
      this.mapControl.ratesUpdated.emit(false);
    });
    this.error = false;
    if (!hotels.length) {
      return;
    }

    if (!hotels[0].id) {
      return;
    }

    if (this.referencePoint?.lat != null && this.referencePoint?.lng != null) {
      this.mapOptions = {
        center: { lat: this.referencePoint.lat, lng: this.referencePoint.lng },
        zoom: 13
      };
    } else if (hotels[0].location?.latitude && hotels[0].location?.longitude) {
      this.mapOptions = {
        center: { lat: +hotels[0].location.latitude, lng: +hotels[0].location.longitude },
        zoom: 13
      };
    }

    if (hotels[0].location.latitude && hotels[0].location.longitude) {
      this.locations = [];
      // Map to count duplicates of lat/lng
      const coordCountMap = new Map<string, number>();
      // Small offset value (~1.5 meter)
      const offsetValue = 0.000015;
      for (let i = 0; i < hotels.length; i++) {
        const hotel = hotels[i];

        const lat = +hotel.location.latitude;
        const lng = +hotel.location.longitude;

        // Generate key for this coordinate
        const coordKey = `${lat}_${lng}`;

        // Get count of duplicates so far
        const duplicateCount = coordCountMap.get(coordKey) || 0;
        coordCountMap.set(coordKey, duplicateCount + 1);

        // Calculate adjusted lat/lng with offset for duplicates
        const adjLat = lat + offsetValue * duplicateCount;
        const adjLng = lng + offsetValue * duplicateCount;

        const thumbUrl = `https://ssl.conferma.com/Images/PropertyThumbnails/${hotel.id.charAt(0)}/${hotel.id.charAt(1)}/${hotel.id}.jpg`;
        const availabilityResult: HotelAvalibilityQuoteResult =
          this.hotelAvailabilityService.getAvailabilityForId(
            this.searchParams.makeHotelAvalilityObject(hotel),
            hotel
          )?.value;

        let price = 0;
        let currencyCode = 'GB';
        const arOfRoomObjs = [];
        if (availabilityResult) {
          price = availabilityResult ? availabilityResult.maxprice : 0;
          currencyCode = availabilityResult.currencyCode;
          if (availabilityResult.rooms) {
            const arrOfRoomKeys = Object.keys(availabilityResult.rooms);
            for (let r = 0; r < arrOfRoomKeys.length; r++) {
              arOfRoomObjs.push(availabilityResult.rooms[arrOfRoomKeys[r]]);
            }
          }
        }

        if (hotel.location) {
          const locTemp = {
            lat: adjLat,
            lng: adjLng,
            label: {
              color: 'purple',
              fontWeight: 'bold',
              fontSize: '12px',
              text: hotel.name
            },
            title: hotel.name,
            ThumbUrl: thumbUrl,
            roomDescs: arOfRoomObjs,
            hotelName: hotel.name,
            guidePrice: price, // hotel.TotalPrice,
            iconUrl: this.getIcon(this.getRoomDetails(availabilityResult), hotel),
            currency: currencyCode,
            holidayCode: hotel.id,
            Code: hotel.id,
            index: this.arrayOfHotels.indexOf(hotel),
            stars: hotel.stars,
            price:
              availabilityResult && availabilityResult.rooms && availabilityResult.rooms.length
                ? availabilityResult.rooms[0].total
                : null
          };

          this.locations.push(locTemp);
        } else {
          console.log('Error cannot find Geocode for this hotel: ', i);
        }
      }
    } else {
      console.log('Error: Cannot find Geocodes!');
      this.error = true;
    }
  }

  public getRoomDetails(hotel): Observable<string | boolean> {
    return this.mapControl.ratesUpdated.pipe(
      map((value) => {
        if (hotel?.rooms) {
          return hotel.rooms;
        } else {
          if (!value) {
            return 'Loading';
          } else {
            return false;
          }
        }
      })
    );
  }

  public openDirections(markerObj: {
    lat: number;
    lng: number;
    index: number | boolean;
    holidayCode: string;
    title: string;
  }): void {
    this.directionsPanelOpen = true;
    this.directionsDestination = {
      lat: markerObj.lat,
      lng: markerObj.lng
    };
    this.directionsDestinationName = markerObj.title;
    this.directionsOrigin = null;
    this.directionsOriginName = null;
    this.directionsURL = '';
    this.directionsResults$ = null;
    if (this.fromInputRef?.nativeElement) {
      this.fromInputRef.nativeElement.value = '';
    }
    if (this.toInputRef?.nativeElement) {
      this.toInputRef.nativeElement.value = markerObj.title;
    }
    setTimeout(() => {
      this.document.getElementById('hotel_map_directions_close').focus();
    }, 0);
  }

  public openDetails(markerObj: {
    lat: number;
    lng: number;
    index: number | boolean;
    holidayCode: string;
    title: string;
  }): void {
    // this.setMapCenter(`${markerObj.lat},${markerObj.lng}`);
    this.mapControl.hotelChosen = markerObj['index'];
    this.mapControl.hotelCodeChosen = markerObj['holidayCode'];
    this.mapControl.showMap = false;
  }

  public setMapCenter(geocode: string): void {
    const position = new google.maps.LatLng(geocode.split(',').shift(), geocode.split(',').pop());
    this.map.panTo(position);
  }

  public getIcon(
    roomObservable: Observable<string | boolean>,
    hotel: HotelItinerary
  ): Observable<string> {
    return roomObservable.pipe(
      map((roomDetails) => {
        if (roomDetails === 'Loading') {
          return `https://chart.apis.google.com/chart?chst=d_map_spin&chld=1.0|0|d0d0d0|8|b|Loading`;
        }
        if (roomDetails[0]?.total && roomDetails[0]?.currencyCode) {
          let pinName;
          if (roomDetails[0].unavailable) {
            if (hotel.preferred?.client) {
              pinName = this.themeService.getRedPreferredClientIcon();
            } else if (hotel.preferred?.consumer) {
              pinName = this.themeService.getRedPreferredConsumerIcon();
            } else {
              pinName = 'hotel-map-pin-red.png';
            }
          } else if (roomDetails[0].requiresReasonKeys?.length) {
            if (hotel.preferred?.client) {
              pinName = this.themeService.getOrangePreferredClientIcon();
            } else if (hotel.preferred?.consumer) {
              pinName = this.themeService.getOrangePreferredConsumerIcon();
            } else {
              pinName = 'hotel-map-pin-orange.png';
            }
          } else {
            if (hotel.preferred?.client) {
              pinName = this.themeService.getGreenPreferredClientIcon();
            } else if (hotel.preferred?.consumer) {
              pinName = this.themeService.getGreenPreferredConsumerIcon();
            } else {
              pinName = 'hotel-map-pin-green.png';
            }
          }
          return `https://images.sabscorp.com/images/enterprise/lightUK/assets/png/${pinName}`;
        } else {
          return `https://images.sabscorp.com/images/enterprise/lightUK/assets/png/hotel-map-pin-loading.png`;
        }
      })
    );
  }

  public loadAPIWrapper(map): void {
    // this.map = map;
  }

  public backToList(): void {
    this.afterBackToList.emit();
  }

  openInfoWindow(marker: MapMarker, infoWindow: MapInfoWindow): void {
    if (this.lastOpenedWindow) {
      this.lastOpenedWindow.close();
    }
    this.lastOpenedWindow = infoWindow;
    infoWindow.open(marker);
  }

  setupAddressSearch(directionsMode: string): void {
    if (directionsMode !== this.directionsMode) {
      this.resetPanel();
      this.directionsMode = directionsMode;
      if (this.directionsOrigin && this.directionsDestination) {
        this.setupDirectionsResults();
      }
    }

    const fromAutocomplete = new google.maps.places.Autocomplete(this.fromInputRef.nativeElement);
    fromAutocomplete.addListener('place_changed', () => {
      this.resetPanel();
      this.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = fromAutocomplete.getPlace();
        this.directionsOrigin = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        this.directionsOriginName = place.formatted_address || place.name;
        this.updateDirectionsURL();
        if (this.directionsDestination) {
          this.setupDirectionsResults();
        }
      });
    });

    const toAutocomplete = new google.maps.places.Autocomplete(this.toInputRef.nativeElement);
    toAutocomplete.addListener('place_changed', () => {
      this.resetPanel();
      this.ngZone.run(() => {
        const place: google.maps.places.PlaceResult = toAutocomplete.getPlace();
        this.directionsDestination = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        };
        this.directionsDestinationName = place.formatted_address || place.name;
        this.updateDirectionsURL();
        if (this.directionsOrigin) {
          this.setupDirectionsResults();
        }
      });
    });
  }

  public swapDirectionsLocations(): void {
    if (!this.directionsOrigin && !this.directionsDestination) {
      return;
    }
    [this.directionsOrigin, this.directionsDestination] = [
      this.directionsDestination,
      this.directionsOrigin
    ];
    [this.directionsOriginName, this.directionsDestinationName] = [
      this.directionsDestinationName,
      this.directionsOriginName
    ];
    if (this.fromInputRef?.nativeElement) {
      this.fromInputRef.nativeElement.value = this.directionsOriginName || '';
    }
    if (this.toInputRef?.nativeElement) {
      this.toInputRef.nativeElement.value = this.directionsDestinationName || '';
    }
    this.updateDirectionsURL();
    this.resetPanel();
    if (this.directionsOrigin && this.directionsDestination) {
      this.setupDirectionsResults();
    } else {
      this.directionsResults$ = null;
    }
  }

  private updateDirectionsURL(): void {
    this.directionsURL =
      this.directionsOrigin && this.directionsDestination
        ? `http://maps.google.com/maps?saddr=${this.directionsOrigin.lat},${this.directionsOrigin.lng}&daddr=${this.directionsDestination.lat},${this.directionsDestination.lng}`
        : '';
  }

  private resetPanel(): void {
    const panelElement: HTMLElement = this.directionsPanelElementRef.nativeElement;
    panelElement.innerHTML = '';
  }

  private setupDirectionsResults(): void {
    const request: google.maps.DirectionsRequest = {
      destination: this.directionsDestination,
      origin: this.directionsOrigin,
      travelMode: google.maps.TravelMode[this.directionsMode]
    };
    this.directionsResults$ = this.mapDirectionsService
      .route(request)
      .pipe(map((response) => response.result));
  }

  clearHotelDirections(): void {
    if (this.directionsDestination) {
      this.map.googleMap.setCenter(this.directionsDestination);
      this.map.googleMap.setZoom(15);
    }
    this.directionsPanelOpen = false;
    this.directionsResults$ = null;
    this.directionsDestination = null;
    this.directionsDestinationName = null;
    this.directionsOrigin = null;
    this.directionsOriginName = null;
    this.directionsURL = '';
    this.directionsMode = google.maps.TravelMode.DRIVING;
    if (this.fromInputRef?.nativeElement) {
      this.fromInputRef.nativeElement.value = '';
    }
    if (this.toInputRef?.nativeElement) {
      this.toInputRef.nativeElement.value = '';
    }
  }
}
